import { useEffect, useEffectEvent, useRef, useState } from 'react';
import { config } from '@/config';
import InfoBox from '@/components/common/InfoBox';
import { DATAKATALOG_STATISTICS_INFO } from './constants';
import {
    datakatalogTabChannelName,
    isDatakatalogTabMessage,
    type DatakatalogTabMessage,
} from './standaloneChannel';

const TOKEN_PATTERN =
    /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const getStandaloneTabParameters = (
    search: string,
    configuredSrc: string,
    environmentConfigs = process.env.config as unknown as
        Record<string, { datakatalogen_url?: string }> | undefined
) => {
    const params = new URLSearchParams(search);
    const token = params.get('datakatalogTab');
    const src = params.get('datakatalogSrc');

    if (!token || !TOKEN_PATTERN.test(token) || !src) return null;

    try {
        const requested = new URL(src);
        const allowedOrigins = [
            configuredSrc,
            ...Object.values(environmentConfigs ?? {}).map(
                environment => environment.datakatalogen_url
            ),
        ].flatMap(url => {
            try {
                const configured = new URL(url ?? '');
                return ['http:', 'https:'].includes(configured.protocol) &&
                    !configured.username &&
                    !configured.password
                    ? [configured.origin]
                    : [];
            } catch {
                return [];
            }
        });
        if (
            !['http:', 'https:'].includes(requested.protocol) ||
            !allowedOrigins.includes(requested.origin) ||
            requested.username ||
            requested.password
        ) {
            return null;
        }
        return { token, src: requested.href, home: requested.origin };
    } catch {
        return null;
    }
};

export const DatakatalogStandalone = () => {
    const parameters = getStandaloneTabParameters(
        window.location.search,
        config.datakatalogen_url
    );
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const channelRef = useRef<BroadcastChannel | null>(null);
    const connectedRef = useRef(true);
    const [disconnected, setDisconnected] = useState(false);
    const [closeRequested, setCloseRequested] = useState(false);
    const [homeCount, setHomeCount] = useState(0);
    const token = parameters?.token;
    const src = parameters?.src;

    const handleCloseRequest = useEffectEvent(() => closeTab('CLOSE'));

    useEffect(() => {
        if (!token || !src) return;
        const channel = new BroadcastChannel(datakatalogTabChannelName(token));
        channelRef.current = channel;
        channel.onmessage = event => {
            if (isDatakatalogTabMessage(event.data)) {
                if (event.data.type === 'DISCONNECT') {
                    connectedRef.current = false;
                    setDisconnected(true);
                } else if (event.data.type === 'CLOSE_TAB') {
                    handleCloseRequest();
                } else if (event.data.type === 'PING' && connectedRef.current) {
                    channel.postMessage({
                        type: 'READY',
                    } satisfies DatakatalogTabMessage);
                }
            }
        };
        channel.postMessage({ type: 'READY' } satisfies DatakatalogTabMessage);

        const handleMessage = (event: MessageEvent) => {
            if (
                event.origin !== new URL(src).origin ||
                !connectedRef.current ||
                !iframeRef.current?.contentWindow ||
                event.source !== iframeRef.current.contentWindow ||
                !isDatakatalogTabMessage(event.data) ||
                event.data.type !== 'ADD_VEGOBJEKT_FROM_DATAKATALOG'
            ) {
                return;
            }
            channel.postMessage({
                type: 'ADD_VEGOBJEKT_FROM_DATAKATALOG',
                payload: event.data.payload,
            } satisfies DatakatalogTabMessage);
        };
        window.addEventListener('message', handleMessage);
        const notifyUnload = () =>
            channel.postMessage({ type: 'TAB_UNLOADED' } satisfies DatakatalogTabMessage);
        window.addEventListener('beforeunload', notifyUnload);
        return () => {
            window.removeEventListener('message', handleMessage);
            window.removeEventListener('beforeunload', notifyUnload);
            channelRef.current = null;
            channel.close();
        };
    }, [token, src]);

    if (!parameters) {
        return (
            <main className="datakatalog-standalone">
                <p>
                    Ugyldig lenke til Datakatalogen. Åpne Datakatalogen fra Vegkart på
                    nytt.
                </p>
            </main>
        );
    }

    const closeTab = (type: 'OPEN_INLINE' | 'CLOSE') => {
        if (!disconnected) {
            channelRef.current?.postMessage({ type } satisfies DatakatalogTabMessage);
        }
        connectedRef.current = false;
        setCloseRequested(true);
        try {
            window.close();
        } catch (error) {
            console.error('Kunne ikke lukke Datakatalog-fanen', error);
        }
    };

    return (
        <main
            className="datakatalog-standalone"
            style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100vh',
            }}
        >
            {!closeRequested && (
                <>
                    <nav
                        aria-label="Datakatalogen"
                        className="datakatalog-overlay__buttons"
                    >
                        <InfoBox
                            className="datakatalog-overlay__info-box"
                            triggerAriaLabel="Vis forklaring av datakatalogstatistikk"
                        >
                            {DATAKATALOG_STATISTICS_INFO}
                        </InfoBox>
                        <button
                            className="datakatalog-overlay__button"
                            aria-label="Gå til startsiden"
                            title="Gå til startsiden"
                            onClick={() => setHomeCount(count => count + 1)}
                        >
                            <i
                                className="material-icons datakatalog-overlay__icon"
                                aria-hidden="true"
                            >
                                home
                            </i>
                        </button>
                        <button
                            className="datakatalog-overlay__button"
                            aria-label="Åpne visning i samme vindu som Vegkart"
                            title="Åpne visning i samme vindu som Vegkart"
                            disabled={disconnected}
                            onClick={() => closeTab('OPEN_INLINE')}
                        >
                            <i
                                className="material-icons datakatalog-overlay__icon"
                                aria-hidden="true"
                            >
                                open_in_browser
                            </i>
                        </button>
                        <button
                            className="datakatalog-overlay__button"
                            aria-label="Lukk datakatalogen"
                            title="Lukk datakatalogen"
                            onClick={() => closeTab('CLOSE')}
                        >
                            <i
                                className="material-icons datakatalog-overlay__icon"
                                aria-hidden="true"
                            >
                                close
                            </i>
                        </button>
                    </nav>
                    <iframe
                        key={homeCount}
                        ref={iframeRef}
                        src={homeCount ? parameters.home : parameters.src}
                        title="Datakatalogen"
                        sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                        style={{ flex: 1, width: '100%', border: 0 }}
                    />
                </>
            )}
            {disconnected && !closeRequested && (
                <p
                    role="alert"
                    style={{
                        margin: 0,
                        padding: '1rem',
                        background: '#fff3cd',
                        color: '#493900',
                        fontWeight: 'bold',
                    }}
                >
                    Frakoblet Vegkart: Forbindelsen til Vegkart er brutt. Åpne
                    Datakatalogen fra Vegkart på nytt.
                </p>
            )}
            {closeRequested && (
                <p role="status">Lukk fanen hvis den ikke ble lukket automatisk.</p>
            )}
        </main>
    );
};
